import { Map } from '@gonfalon/immutable-shim/immutableShim';
import type { AnyAction } from 'redux';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';

import { experiments as actionTypes } from '#actionTypes/experiments';
import { createRequestReducer } from '#reducers/createRequestReducer';
import registry from '#reducers/registry';
import { searchSelector } from '#reducers/router';
import type { Experiment } from '#utils/experimentDashboardUtils';
import { createExperimentFiltersFromQuery } from '#utils/experimentDashboardUtils';

import paginate from './paginate';

const experimentsEntities = (state: Map<string, Experiment> = Map(), action: AnyAction): Map<string, Experiment> => {
  if (actionTypes.RECEIVE_EXPERIMENTS === action.type) {
    if (action.response && action.response.hasIn(['entities', 'experiments'])) {
      return action.response.getIn(['entities', 'experiments']);
    }
  }
  return state;
};

const request = createRequestReducer([
  actionTypes.REQUEST_EXPERIMENTS,
  actionTypes.RECEIVE_EXPERIMENTS,
  actionTypes.REQUEST_EXPERIMENTS_FAILED,
]);

const experimentsPagination = paginate({
  types: [actionTypes.REQUEST_EXPERIMENTS, actionTypes.REQUEST_EXPERIMENTS_FAILED, actionTypes.RECEIVE_EXPERIMENTS],
  mapActionToKey: () => 'all',
});

const experimentsSummaryCounts = (state: Map<string, number> = Map(), action: AnyAction) => {
  if (actionTypes.RECEIVE_EXPERIMENTS === action.type) {
    if (action.response && action.response.hasIn(['result', '_summaryCounts'])) {
      return action.response.getIn(['result', '_summaryCounts']);
    }
  }
  return state;
};

export const experiments = combineReducers({
  entities: experimentsEntities,
  pagination: experimentsPagination,
  request,
  summaryCounts: experimentsSummaryCounts,
});

import type { GlobalState } from '#reducers/state';

const experimentsSelector = (state: GlobalState) => state.experiments;

const experimentEntitiesSelector = (state: GlobalState) => experimentsSelector(state).entities;
export const experimentRequestSelector = (state: GlobalState) => experimentsSelector(state).request;
export const experimentListSelector = (state: GlobalState) => experimentEntitiesSelector(state).toList();

export const experimentFilterSelector = createSelector(searchSelector, (query) =>
  createExperimentFiltersFromQuery(query),
);

registry.addReducers({ experiments });

declare module '#reducers/state' {
  interface GlobalState {
    experiments: ReturnType<typeof experiments>;
  }
}
